import { useReducer, useState, useCallback, useEffect, useRef } from "react";
import { authClient } from "@/auth/client";
import { needsOnboarding, splitFullNameForProfile } from "@/lib/onboarding";
import { otpAuthReducer, initialOtpState, mapOtpError } from "@/lib/otp-auth-machine";
import { toast } from "@/lib/toast";
import i18n from "@/i18n";

const RESEND_COOLDOWN_SECONDS = 60;

interface ProfileResp {
  role: string;
  firstName: string | null;
  lastName: string | null;
  phone: string | null;
}

/**
 * Best effort: prijava je već uspjela, pa neuspjelo spremanje imena ne prekida
 * tok, ali korisnik mora znati da ime nije spremljeno (onboarding ga traži kasnije).
 */
async function completeClientProfileFromGuestName(name: string): Promise<void> {
  const split = splitFullNameForProfile(name);
  if (!split) return;

  const res = await fetch("/api/me/profile");
  if (!res.ok) return;
  const profile = (await res.json()) as ProfileResp;
  if (!needsOnboarding(profile)) return;

  try {
    const saveRes = await fetch("/api/me/profile", {
      method: "PATCH",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        firstName: split.firstName,
        lastName: split.lastName,
        phone: profile.phone,
      }),
    });
    if (!saveRes.ok) {
      const body = await saveRes.json().catch(() => null);
      const reason = typeof body?.error === "string" ? ` ${body.error}` : "";
      toast.error(`${i18n.t("booking.profileSaveFailed")}${reason}`);
    }
  } catch {
    toast.error(i18n.t("booking.profileSaveFailed"));
  }
}

export async function requestGuestOtpCode(email: string): Promise<boolean> {
  const { error } = await authClient.emailOtp.sendVerificationOtp({
    email: email.trim(),
    type: "sign-in",
  });
  return !error;
}

/**
 * Drives the guest email-OTP flow: collect email/name → send code → verify.
 * On successful verification the account is created (if new) and signed in,
 * then `onAuthenticated` is called.
 */
export function useGuestOtpAuth(
  onAuthenticated: () => void,
  options: { completeClientProfile?: boolean } = {},
) {
  const { completeClientProfile = true } = options;
  const [state, dispatch] = useReducer(otpAuthReducer, initialOtpState);
  const [cooldown, setCooldown] = useState(0);

  const onAuthRef = useRef(onAuthenticated);
  onAuthRef.current = onAuthenticated;

  useEffect(() => {
    if (cooldown <= 0) return;
    const t = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  const requestCode = useCallback(async (): Promise<boolean> => {
    const ok = await requestGuestOtpCode(state.email);
    if (!ok) return false;
    setCooldown(RESEND_COOLDOWN_SECONDS);
    return true;
  }, [state.email]);

  const sendCode = useCallback(async () => {
    dispatch({ type: "SEND_START" });
    const ok = await requestCode();
    dispatch(ok ? { type: "SEND_OK" } : { type: "SEND_FAIL", errorKey: "booking.otpErrorSend" });
  }, [requestCode]);

  const resend = useCallback(async () => {
    if (cooldown > 0) return;
    await requestCode();
  }, [cooldown, requestCode]);

  const verifyCode = useCallback(
    async (otp: string) => {
      dispatch({ type: "VERIFY_START" });
      try {
        const { error } = await authClient.signIn.emailOtp({
          email: state.email.trim(),
          otp,
        });
        if (error) throw new Error(error.message || "verify failed");

        // Top up the name for newly created accounts (empty name), never
        // overwrite a returning user's existing name.
        const typedName = state.name.trim();
        if (typedName) {
          // Read fresh server state (not the cookie cache) so we never
          // overwrite a returning user's existing name.
          const session = await authClient.getSession({
            query: { disableCookieCache: true },
          });
          const currentName = session?.data?.user?.name?.trim();
          if (!currentName) {
            await authClient.updateUser({ name: typedName });
          }
          if (completeClientProfile) {
            await completeClientProfileFromGuestName(typedName);
          }
        }
        onAuthRef.current();
      } catch (e) {
        dispatch({ type: "VERIFY_FAIL", errorKey: mapOtpError(e) });
      }
    },
    [state.email, state.name, completeClientProfile],
  );

  return {
    phase: state.phase,
    email: state.email,
    name: state.name,
    errorKey: state.errorKey,
    cooldown,
    setEmail: (email: string) => dispatch({ type: "SET_EMAIL", email }),
    setName: (name: string) => dispatch({ type: "SET_NAME", name }),
    editEmail: () => dispatch({ type: "EDIT_EMAIL" }),
    sendCode,
    resend,
    verifyCode,
  };
}
